import { buildPointCardPages } from '@/components/Functional/utils/pointCard'
import { Button } from '@/components/UI/Button'
import { Stamp } from '@/components/UI/Stamp'
import { Typography } from '@/components/UI/Typography'
import type { StampInSheetData, UserSheetHistoryData } from '@/schemas/v2'
import { Pagination } from '@mantine/core'
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

export type PointCardStampInfo = {
  id: number
  name: string
  image: string | null
  point: number
  isHas: boolean
  /** 獲得日時（ISO8601）。スタンプ詳細ダイアログで表示する */
  acquiredAt: string | null
  spots: StampInSheetData['spots']
}

type Props = {
  sheetName: string
  /** GET /api/v2/user/sheets の history（1 要素 = 1 獲得） */
  history: UserSheetHistoryData[]
  /** GET /api/v2/sheets のマスタスタンプ（spots / point の解決に使用） */
  stamps: StampInSheetData[]
  /** meta.totalStampCount（獲得数表示に使用） */
  acquiredCount: number
  stampShape: 'circular' | 'square'
  backgroundImage?: string | null
  /**
   * 未獲得枠に表示する「スタンプなし画像」URL。
   * GET /api/v2/sheets の stamps[].image（キャンペーン設定 → システムデフォルトの順で解決済み）を渡す。
   * null のときはグレープレースホルダーを表示する
   */
  nonStampImage?: string | null
  /** 獲得済み枠をタップした時に呼ばれる。スタンプ詳細ダイアログ表示に使用する */
  onStampClick: (stamp: PointCardStampInfo) => void
  /** 「獲得履歴を表示する」ボタン押下時のコールバック */
  onClickHistory: () => void
  /**
   * カードのページ数が変わってスライドの高さが変化した時に呼ばれる。
   * 親の Swiper(autoHeight) に updateAutoHeight() させるために使用する（OVERLAY の onOverlayImageLoad と同パターン）。
   */
  onLayoutChange?: () => void
  /** 初期表示するカードのページ index（0 始まり）。省略時は最新ページ。Storybook での中間ページ再現用 */
  initialPageIndex?: number
}

/**
 * ページャー前後ボタンの見た目（#2970）。枠線・背景を消して矢印のみにする。
 * Mantine の hover 背景（:hover 付きセレクタ）にも勝つよう important を付ける
 */
const PAGER_CONTROL_CLASS = '!border-0 !bg-transparent enabled:text-main'

/** Mantine 既定の矢印より一回り大きい矢印（Pagination の icon に渡す。Mantine からの props は使わない） */
const PagerPrevIcon = () => <IconChevronLeft size={28} stroke={2} aria-hidden="true" />
const PagerNextIcon = () => <IconChevronRight size={28} stroke={2} aria-hidden="true" />

/**
 * ポイントカード（sheet.type === 'point_card'）の台紙 UI（#2970）。
 *
 * - 1 枚 = 15 枠（3 列 × 5 行）固定。獲得した順（古い順）に枠が埋まる
 * - 同一スタンプの複数回獲得も 1 獲得 = 1 枠として埋める
 * - 15 枠を超えるとカードが増え、前後ボタンでページ送りする（親の横 Swiper とジェスチャーが競合しないようボタン方式）
 * - 枠下にラベル（スタンプ名・獲得日時）は出さない。獲得日時はスタンプ詳細ダイアログで表示する
 */
export const PointCardSheet = ({
  sheetName,
  history,
  stamps,
  acquiredCount,
  stampShape,
  backgroundImage,
  nonStampImage,
  onStampClick,
  onClickHistory,
  onLayoutChange,
  initialPageIndex,
}: Props) => {
  const { t } = useTranslation()
  const pages = useMemo(() => buildPointCardPages(history, stamps), [history, stamps])

  // 初期表示は「いま埋めている最新のカード」＝最新ページ（例: 16 個獲得 → 2 ページ目）。
  // ページ数が減っても範囲外にならないよう clamp する
  const [pageIndex, setPageIndex] = useState(initialPageIndex ?? pages.length - 1)
  const currentPage = Math.min(pageIndex, pages.length - 1)
  const slots = pages[currentPage]

  // ページ数の増減（インジケーター行の出現）でスライド高さが変わるため、親 Swiper に測り直させる
  // biome-ignore lint/correctness/useExhaustiveDependencies: ページ数の変化をトリガーにするための意図的な依存
  useEffect(() => {
    onLayoutChange?.()
  }, [pages.length, onLayoutChange])

  const style = backgroundImage
    ? { backgroundImage: `url("${backgroundImage}")`, backgroundRepeat: 'repeat-y', backgroundSize: '100% auto' }
    : { backgroundColor: '#ffffff' }

  return (
    <div className="rounded-lg px-4 py-6 border border-[#cbcbce] relative overflow-hidden" style={style}>
      {/* シート名（中央揃え） */}
      <Typography variant="body4" component="h2" className="text-center">
        {sheetName}
      </Typography>

      {/* 累計獲得ポイント（1 獲得 = 1 ポイント運用のため値は meta.totalStampCount）。
          PointRallyCourseCard のポイント表示と同じ体裁（大きい数値 + 単位を下揃え・右寄せ）。ポイント表記のみで切り替えは無い */}
      <div className="my-[8px] flex items-end justify-end gap-[8px] text-black leading-none">
        <Typography variant="h1" component="span">
          {acquiredCount.toLocaleString()}
        </Typography>
        <Typography variant="body3" component="span">
          {t('common.point', 'ポイント')}
        </Typography>
      </div>

      {pages.length > 1 && (
        // Mantine Pagination の複合コンポーネントで「< 1/10 >」のコンパクト表記にする。
        // 前後ボタンは Pagination.Root の active/total から自動で端で disabled になる
        <Pagination.Root total={pages.length} value={currentPage + 1} onChange={(page) => setPageIndex(page - 1)}>
          <div className="mt-2 flex items-center justify-center gap-3">
            {/* 枠線・背景なしで矢印のみ表示し、矢印は少し大きくする（メインカラー）。
                端で disabled のときは Mantine 既定の淡色のまま */}
            <Pagination.Previous
              icon={PagerPrevIcon}
              aria-label={t('home.point_card.prev_page', '前のカード')}
              className={PAGER_CONTROL_CLASS}
            />
            {/* 分母の桁数に合わせて分子を figure space（数字と同幅の空白 U+2007）で左詰めする。
                tabular-nums の等幅数字と合わせてラベル幅が常に一定になり、「/」も矢印もガタつかない */}
            <Typography variant="button1" component="span" className="tabular-nums">
              {t('home.point_card.page_indicator', '{{current}}/{{total}}', {
                current: String(currentPage + 1).padStart(String(pages.length).length, '\u2007'),
                total: pages.length,
              })}
            </Typography>
            <Pagination.Next
              icon={PagerNextIcon}
              aria-label={t('home.point_card.next_page', '次のカード')}
              className={PAGER_CONTROL_CLASS}
            />
          </div>
        </Pagination.Root>
      )}

      {/* ページャーとスタンプ枠の間に累計獲得数を表示する（1 ページのみでページャーが無い場合も表示） */}
      <Typography variant="body3" component="p" className="mt-[8px] text-right text-black">
        {t('home.point_card.acquired_count', '獲得数：{{total}}個', { total: acquiredCount.toLocaleString() })}
      </Typography>

      <div className="mt-4 grid grid-cols-3 items-start justify-items-center gap-y-4">
        {slots.map((slot, i) =>
          slot.acquired ? (
            <button
              key={slot.transactionId}
              type="button"
              className="flex w-full flex-col items-center justify-start"
              onClick={() =>
                onStampClick({
                  id: slot.stampId,
                  name: slot.name,
                  image: slot.image,
                  point: slot.point,
                  isHas: true,
                  acquiredAt: slot.acquiredAt,
                  spots: slot.spots,
                })
              }
            >
              <Stamp type="Get" src={slot.image ?? undefined} size="Medium" shape={stampShape} alt={slot.name} />
            </button>
          ) : (
            // biome-ignore lint/suspicious/noArrayIndexKey: 未獲得枠は静的プレースホルダーのため index を key に使用
            <div key={`empty-${currentPage}-${i}`} className="flex w-full flex-col items-center justify-start">
              {/* 未獲得枠は通常のスタンプシートと同じ「スタンプなし画像」を表示する（無ければグレープレースホルダー） */}
              {nonStampImage ? (
                <Stamp type="Get" src={nonStampImage} size="Medium" shape={stampShape} />
              ) : (
                <Stamp type="None" size="Medium" shape={stampShape} />
              )}
            </div>
          ),
        )}
      </div>

      {/* 獲得履歴ボタン（PointRallyCourseCard と同一の体裁: 白抜き primaryOutlined / fullWidth / size 既定 md=48px） */}
      <div className="mt-6">
        <Button
          label={t('home.show_point_history', '獲得履歴を表示する')}
          variant="primaryOutlined"
          fullWidth
          onClick={onClickHistory}
        />
      </div>
    </div>
  )
}
